import { useEffect, useMemo, useState } from 'react';
import { Database, Search, ShieldCheck } from 'lucide-react';
import Alert from '../components/common/Alert';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Input from '../components/forms/Input';
import Select from '../components/forms/Select';
import Table from '../components/tables/Table';
import { auditService } from '../services';
import { userService } from '../services';
import { useAuth } from '../context/AuthContext';

const actionLabels = {
  create: 'Creación', edit: 'Edición', assign: 'Asignación',
  resolve: 'Resolución', validate: 'Validación', reopen: 'Reapertura',
  remove: 'Eliminación', configure: 'Configuración',
};
const moduleLabels = {
  user: 'Usuarios', voter: 'Votantes', duplicate: 'Duplicados',
  sheet: 'Planillas', driver: 'Conductores', vehicle: 'Móviles',
  mobilization: 'Movilización',
};
const pretty = (value) => value == null ? 'Sin datos' : JSON.stringify(value, null, 2);

function AuditPage() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    query: '', module: '', action: '', userId: '', fromDate: '', toDate: '',
  });
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    userService.getForActor(user, { pageSize: 1000 })
      .then((response) => setUsers(response.data))
      .catch(() => setUsers([]));
  }, [user]);
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      auditService.getAll(filters)
        .then((response) => setRecords(response.data))
        .catch((requestError) => setError(requestError.message))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(timer);
  }, [filters]);

  const names = useMemo(() => new Map(users.map((item) => [item.id, item.name])), [users]);
  const columns = [
    {
      key: 'created_at', header: 'Fecha',
      render: (value) => new Date(value).toLocaleString('es-PY'),
    },
    {
      key: 'user_id', header: 'Usuario',
      render: (value) => names.get(value) || value || 'Sistema',
    },
    {
      key: 'action', header: 'Acción',
      render: (value) => actionLabels[value] || value,
    },
    {
      key: 'module', header: 'Módulo',
      render: (value) => moduleLabels[value] || value,
    },
    { key: 'record_id', header: 'Registro' },
    { key: 'detail', header: 'Descripción' },
    {
      key: 'data', header: 'Cambios',
      render: (_, row) => (
        <details className="min-w-56">
          <summary className="cursor-pointer font-medium text-primary-700">Ver datos</summary>
          <div className="mt-2 grid gap-2 text-xs">
            <div><p className="font-semibold">Datos anteriores</p><pre className="mt-1 max-h-40 overflow-auto rounded bg-slate-950 p-2 text-slate-100">{pretty(row.previous_data)}</pre></div>
            <div><p className="font-semibold">Datos nuevos</p><pre className="mt-1 max-h-40 overflow-auto rounded bg-slate-950 p-2 text-slate-100">{pretty(row.new_data)}</pre></div>
          </div>
        </details>
      ),
    },
  ];
  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold text-primary-600">Trazabilidad del sistema</p>
        <h1 className="mt-1 text-2xl font-bold">Auditoría</h1>
        <p className="mt-1 text-sm text-content-muted">Registro centralizado de las operaciones sensibles.</p>
      </div>
      {error && <Alert variant="danger" title="No se pudo consultar la auditoría">{error}</Alert>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card><div className="flex items-center gap-4"><span className="grid size-11 place-items-center rounded-control bg-primary-50 text-primary-700"><Database className="size-5" /></span><div><p className="text-sm text-content-muted">Operaciones encontradas</p><p className="text-2xl font-bold">{records.length}</p></div></div></Card>
        <Card><div className="flex items-center gap-4"><span className="grid size-11 place-items-center rounded-control bg-success-50 text-success-700"><ShieldCheck className="size-5" /></span><div><p className="text-sm text-content-muted">Persistencia</p><p className="font-semibold">Auditoría centralizada</p></div></div></Card>
      </div>
      <Card title="Filtros" description="Busca por módulo, acción, usuario, registro o intervalo de fechas.">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Input id="audit-query" label="Buscar" icon={Search} placeholder="Registro, acción o descripción" value={filters.query} onChange={(event) => setFilter('query', event.target.value)} />
          <Select id="audit-module" label="Módulo" placeholder="Todos" value={filters.module} onChange={(event) => setFilter('module', event.target.value)}
            options={Object.entries(moduleLabels).map(([value, label]) => ({ value, label }))} />
          <Select id="audit-action" label="Acción" placeholder="Todas" value={filters.action} onChange={(event) => setFilter('action', event.target.value)}
            options={Object.entries(actionLabels).map(([value, label]) => ({ value, label }))} />
          <Select id="audit-user" label="Usuario" placeholder="Todos" value={filters.userId} onChange={(event) => setFilter('userId', event.target.value)}
            options={users.map((item) => ({ value: item.id, label: item.name }))} />
          <Input id="audit-from" type="date" label="Desde" value={filters.fromDate} onChange={(event) => setFilter('fromDate', event.target.value)} />
          <Input id="audit-to" type="date" label="Hasta" value={filters.toDate} onChange={(event) => setFilter('toDate', event.target.value)} />
        </div>
      </Card>
      <Card title="Historial de operaciones">
        {loading ? <div className="grid min-h-48 place-items-center"><LoadingSpinner label="Consultando auditoría..." /></div>
          : <Table columns={columns} data={records} emptyMessage="No hay operaciones para los filtros seleccionados." />}
      </Card>
    </div>
  );
}

export default AuditPage;
